<template>
  <a-drawer width="35%" :label-col="4" :wrapper-col="14" :visible="open" @close="onClose">
    <a-divider orientation="left">
      <b>{{ $t(formTitleKey) }}</b>
    </a-divider>
    <a-form-model ref="form" :model="form" :rules="formRules">
      <a-form-model-item :label="$t('views.specialMultiple.query.type')" prop="type">
        <a-select
          v-model="form.type"
          allow-clear
          :placeholder="$t('views.specialMultiple.query.typePlaceholder')"
          style="width: 100%"
        >
          <a-select-option :value="0">{{ $t('views.specialMultiple.type.0') }}</a-select-option>
          <a-select-option :value="1">{{ $t('views.specialMultiple.type.1') }}</a-select-option>
          <a-select-option :value="2">{{ $t('views.specialMultiple.type.2') }}</a-select-option>
          <a-select-option :value="3">{{ $t('views.specialMultiple.type.3') }}</a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.specialMultiple.form.name')" prop="productType">
        <a-input
          v-model="form.productType"
          :placeholder="$t('views.specialMultiple.form.namePlaceholder')"
        />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.specialMultiple.column.subType')" prop="subType">
        <a-input
          v-model="form.subType"
          :placeholder="$t('views.specialMultiple.form.subTypePlaceholder')"
        />
      </a-form-model-item>
      <template v-if="isSpecialSizeType">
        <a-form-model-item :label="$t('views.specialMultiple.column.expression')" prop="expression">
          <a-input
            v-model="form.expression"
            readonly
            disabled
            :placeholder="$t('views.specialMultiple.form.expressionBuilder.placeholder')"
          />
          <div style="margin-top: 8px">
            <a-space wrap :size="8">
              <a-button size="small" @click="appendExpressionToken('front')">{{ $t('views.specialMultiple.form.expressionToken.front') }}</a-button>

              <a-button size="small" @click="appendExpressionToken('back')">{{ $t('views.specialMultiple.form.expressionToken.back') }}</a-button>
              <a-button size="small" @click="appendExpressionToken('bottom')">{{ $t('views.specialMultiple.form.expressionToken.bottom') }}</a-button>
              <a-button size="small" @click="appendExpressionToken('+')">{{ $t('views.specialMultiple.form.expressionToken.plus') }}</a-button>
              <a-button size="small" @click="appendExpressionToken('-')">{{ $t('views.specialMultiple.form.expressionToken.minus') }}</a-button>
              <a-button size="small" @click="appendExpressionToken('*')">{{ $t('views.specialMultiple.form.expressionToken.multiply') }}</a-button>
              <a-button size="small" @click="appendExpressionToken('/')">{{ $t('views.specialMultiple.form.expressionToken.divide') }}</a-button>
              <a-button size="small" @click="undoExpressionLast">{{ $t('views.specialMultiple.form.expressionUndo') }}</a-button>
              <a-button size="small" type="dashed" @click="clearExpression">{{ $t('views.specialMultiple.form.expressionClear') }}</a-button>
            </a-space>
          </div>
        </a-form-model-item>
        <a-form-model-item :label="$t('views.specialMultiple.query.lowerLimitExclusive')" prop="lowerLimitExclusive">
          <a-input-number
            v-model="form.lowerLimitExclusive"
            :step="0.01"
            :precision="2"
            allow-clear
            style="width: 100%"
            :placeholder="$t('views.specialMultiple.query.lowerLimitExclusivePlaceholder')"
          />
        </a-form-model-item>
        <a-form-model-item :label="$t('views.specialMultiple.query.upperLimitInclusive')" prop="upperLimitInclusive">
          <a-input-number
            v-model="form.upperLimitInclusive"
            :step="0.01"
            :precision="2"
            allow-clear
            style="width: 100%"
            :placeholder="$t('views.specialMultiple.query.upperLimitInclusivePlaceholder')"
          />
        </a-form-model-item>
      </template>
      <a-form-model-item v-if="showMergeCountField" :label="$t('views.specialMultiple.column.mode')" prop="mode">
        <a-input-number
          v-model="form.mode"
          :min="0"
          :step="1"
          :precision="0"
          allow-clear
          style="width: 100%"
          :placeholder="$t('views.specialMultiple.query.modePlaceholder')"
        />
      </a-form-model-item>
      <a-form-model-item v-if="showSpecOptionsListField" :label="$t('views.specialMultiple.column.specOptionsList')" prop="specOptionsList">
        <a-select
          v-model="specOptionIdList"
          mode="multiple"
          allow-clear
          show-search
          :placeholder="$t('views.specialMultiple.query.specOptionsListPlaceholder')"
          :loading="specOptionLoading"
          option-filter-prop="_label"
          option-label-prop="_label"
          :filter-option="filterSpecOptionOption"
          style="width: 100%"
          :getPopupContainer="trigger => trigger.parentNode"
          @dropdownVisibleChange="onSpecOptionDropdownVisible"
        >
          <a-select-option
            v-for="opt in specOptionOptions"
            :key="opt.id"
            :value="String(opt.id)"
            :_label="opt.name"
          >
            {{ opt.name }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.specialMultiple.query.multiplier')" prop="multiplier">
        <a-input-number
          v-model="form.multiplier"
          :min="0"
          :step="0.01"
          :precision="2"
          allow-clear
          style="width: 100%"
          :placeholder="$t('views.specialMultiple.query.multiplierPlaceholder')"
        />
      </a-form-model-item>
      <div class="bottom-control">
        <a-space>
          <a-button type="primary" :loading="submitLoading" @click="submitForm">
            {{ $t('views.specialMultiple.form.save') }}
          </a-button>
          <a-button type="dashed" @click="cancel">
            {{ $t('views.specialMultiple.form.cancel') }}
          </a-button>
        </a-space>
      </div>
    </a-form-model>
  </a-drawer>
</template>

<script>
import { getSpecialMultiple, addSpecialMultiple, updateSpecialMultiple } from '@/api/user/specialMultiple'
import { listSpecOption } from '@/api/user/specOption'

const SPECIAL_SIZE_TYPE = 3
const SPECIAL_PROCESS_TYPE = 2

export default {
  name: 'CreateForm',
  props: {
  },
  components: {
  },
  data () {
    return {
      submitLoading: false,
      formTitleKey: 'views.specialMultiple.title.add',
      form: {
        id: null,
        type: undefined,
        categoryId: null,
        productType: null,
        subType: null,
        expression: null,
        lowerLimitExclusive: null,
        upperLimitInclusive: null,
        mode: null,
        multiplier: null,
        specOptionsList: null,
        sort: null,
        status: 1,
        createTime: null,
        remark: null
      },
      formType: 1,
      open: false,
      specOptionOptions: [],
      specOptionIdList: [],
      specOptionLoading: false
    }
  },
  computed: {
    isSpecialSizeType () {
      const t = this.form.type
      return t === SPECIAL_SIZE_TYPE || t === String(SPECIAL_SIZE_TYPE)
    },
    isSpecialProcessType () {
      const t = this.form.type
      return t === SPECIAL_PROCESS_TYPE || t === String(SPECIAL_PROCESS_TYPE)
    },
    showMergeCountField () {
      return this.isSpecialProcessType || this.isSpecialSizeType
    },
    /** 特殊材料/特殊辅料/特殊工艺：规格选项多选；特殊尺寸不展示（用表达式区间） */
    showSpecOptionsListField () {
      return !this.isSpecialSizeType
    },
    formRules () {
      return {
        type: [
          { required: true, message: this.$t('views.specialMultiple.rules.typeRequired'), trigger: 'change' }
        ]
      }
    }
  },
  watch: {
    specOptionIdList (val) {
      if (this.isSpecialSizeType) {
        return
      }
      const list = Array.isArray(val) ? val.map(String).filter(Boolean) : []
      this.form.specOptionsList = list.length ? list.join(',') : null
    },
    'form.type' () {
      if (this.isSpecialSizeType) {
        this.specOptionIdList = []
        this.form.specOptionsList = null
      }
    }
  },
  methods: {
    onClose () {
      this.open = false
    },
    cancel () {
      this.open = false
      this.reset()
    },
    reset () {
      this.formType = 1
      this.formTitleKey = 'views.specialMultiple.title.add'
      this.specOptionIdList = []
      this.form = {
        id: null,
        type: undefined,
        categoryId: null,
        productType: null,
        subType: null,
        expression: null,
        lowerLimitExclusive: null,
        upperLimitInclusive: null,
        mode: null,
        multiplier: null,
        specOptionsList: null,
        sort: null,
        status: 1,
        createTime: null,
        remark: null
      }
    },
    parseSpecOptionsListToIdList (str) {
      if (str === null || str === undefined || str === '') {
        return []
      }
      const s = String(str).trim()
      if (!s) {
        return []
      }
      return s.split(',').map(x => String(x).trim()).filter(Boolean)
    },
    syncSpecOptionIdListFromForm () {
      this.specOptionIdList = this.parseSpecOptionsListToIdList(this.form.specOptionsList)
    },
    loadSpecOptionOptions () {
      if (this.specOptionLoading) {
        return
      }
      this.specOptionLoading = true
      listSpecOption({}).then(res => {
        const data = (res && res.data) || []
        this.specOptionOptions = Array.isArray(data) ? data : []
      }).finally(() => {
        this.specOptionLoading = false
      })
    },
    onSpecOptionDropdownVisible (open) {
      if (open && !this.specOptionOptions.length && !this.specOptionLoading) {
        this.loadSpecOptionOptions()
      }
    },
    filterSpecOptionOption (input, option) {
      const text =
        option &&
        option.componentOptions &&
        option.componentOptions.children &&
        option.componentOptions.children[0] &&
        option.componentOptions.children[0].text
      return (text || '').toLowerCase().includes((input || '').toLowerCase())
    },
    appendExpressionToken (token) {
      const cur = this.form.expression == null ? '' : String(this.form.expression)
      this.form.expression = cur + token
    },
    clearExpression () {
      this.form.expression = null
    },
    undoExpressionLast () {
      let cur = this.form.expression == null ? '' : String(this.form.expression)
      if (!cur) {
        return
      }
      let next = cur.replace(/(front|back|bottom|\+|\-|\/|\*)$/, '')
      if (next === cur) {
        next = cur.slice(0, -1)
      }
      this.form.expression = next === '' ? null : next
    },
    fillExpressionExample () {
      this.form.expression = 'front+back+bottom'
    },
    buildSubmitPayload () {
      const f = this.form
      const payload = { ...f }
      const isSize = f.type === SPECIAL_SIZE_TYPE || f.type === String(SPECIAL_SIZE_TYPE)
      const isProcess = f.type === SPECIAL_PROCESS_TYPE || f.type === String(SPECIAL_PROCESS_TYPE)
      if (isSize) {
        payload.specOptionsList = null
      } else {
        const ids = Array.isArray(this.specOptionIdList) ? this.specOptionIdList.map(String).filter(Boolean) : []
        payload.specOptionsList = ids.length ? ids.join(',') : null
      }
      if (!isSize) {
        payload.expression = null
        payload.lowerLimitExclusive = null
        payload.upperLimitInclusive = null
      }
      if (!isSize && !isProcess) {
        payload.mode = null
      }
      return payload
    },
    handleAdd () {
      this.reset()
      this.formType = 1
      this.formTitleKey = 'views.specialMultiple.title.add'
      this.loadSpecOptionOptions()
      this.open = true
    },
    handleUpdate (row, ids) {
      this.reset()
      this.formType = 2
      this.formTitleKey = 'views.specialMultiple.title.edit'
      const id = row ? row.id : ids
      getSpecialMultiple({ id: id }).then(response => {
        this.form = response.data
        this.syncSpecOptionIdListFromForm()
        if (this.isSpecialSizeType) {
          this.specOptionIdList = []
          this.form.specOptionsList = null
        }
        this.loadSpecOptionOptions()
        this.open = true
      })
    },
    submitForm () {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return false
        }
        this.submitLoading = true
        const payload = this.buildSubmitPayload()
        if (this.form.id !== undefined && this.form.id !== null) {
          updateSpecialMultiple(payload).then(response => {
            if (response.success) {
              this.$message.success(this.$t('views.specialMultiple.msg.updateOk'), 3)
              this.open = false
              this.$emit('ok')
            } else {
              const messageInfo = response.message || response.msg || this.$t('views.specialMultiple.msg.updateFail')
              this.$message.error(messageInfo)
            }
          }).finally(() => {
            this.submitLoading = false
          })
        } else {
          addSpecialMultiple(payload).then(response => {
            if (response.success) {
              this.$message.success(this.$t('views.specialMultiple.msg.addOk'), 3)
              this.open = false
              this.$emit('ok')
            } else {
              const messageInfo = response.message || response.msg || this.$t('views.specialMultiple.msg.addFail')
              this.$message.error(messageInfo)
            }
          }).finally(() => {
            this.submitLoading = false
          })
        }
      })
    }
  }
}
</script>
